import React, { useState, useCallback } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import { LazyLoadImage } from "react-lazy-load-image-component";
import FormInput from "../Input/formInput";
import ProductMultiSelectionModal from "./productMultiSelectionModel";
import CustomModal from "./customModal";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";

const RequisitionProductSelector = ({
  lines = [],
  setLines,
  header,
  requisitionId,
  compID,
  branchCompId,
  parentCompID,
  isMain,
  products = [],
  onRefresh,
  setLoading,
  showAddModal: externalShowModal,
  setShowAddModal: setExternalShowModal,
  onSend,
  onReceive,
}) => {
  const [internalShowModal, setInternalShowModal] = useState(false);
  const showModal = externalShowModal !== undefined ? externalShowModal : internalShowModal;
  const setShowModal = setExternalShowModal || setInternalShowModal;
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [editRequiredQty, setEditRequiredQty] = useState("");
  const [updatingLineId, setUpdatingLineId] = useState(null);
  const [lineUpdates, setLineUpdates] = useState({});
  const [savingSendQty, setSavingSendQty] = useState(false);
  const [savingReceiveQty, setSavingReceiveQty] = useState(false);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const pickStr = (...candidates) => {
    for (const v of candidates) {
      if (v == null) continue;
      const s = String(v).trim();
      if (s !== "") return s;
    }
    return "";
  };

  /** Line rows from API: camelCase, PascalCase, or blank with only MenuItem_ID — match all common keys. */
  const getDisplayName = (line) => {
    const name = pickStr(
      line?.menuItem_Name,
      line?.MenuItem_Name,
      line?.menuItemName,
      line?.MenuItemName,
      line?.menuItem_DefaultVariantTitle,
      line?.MenuItem_DefaultVariantTitle,
      line?.menu_item_name
    );
    if (name) return name;
    const mid = line?.menuItem_ID ?? line?.MenuItem_ID;
    return mid != null && mid !== "" ? `Item #${mid}` : "—";
  };

  const getLineUnit = (line) =>
    pickStr(
      line?.unit,
      line?.Unit,
      line?.defaultSizeUnit,
      line?.DefaultSizeUnit,
      line?.menuItem_DefaultSizeUnit,
      line?.MenuItem_DefaultSizeUnit
    ) || "—";

  const status = (header?.status ?? header?.Status ?? "").toString();
  const isDraft = status.toLowerCase() === "draft";
  const isAccepted = status.toLowerCase() === "accepted";
  const isSent = status.toLowerCase() === "sent";
  const canAddItems = isDraft || (isAccepted && isMain);
  const canEditRequiredQty = isDraft || (isAccepted && isMain);

  const handleModalSubmit = useCallback(
    async (newSelectedProducts) => {
      if (!requisitionId || !branchCompId || !parentCompID) return false;
      try {
        if (setLoading) setLoading(true);
        for (const p of newSelectedProducts || []) {
          const mid = p.menuItem_ID ?? p.MenuItem_ID;
          const mname = p.menuItem_DefaultVariantTitle ?? p.MenuItem_DefaultVariantTitle ?? p.menuItem_Name ?? p.MenuItem_Name;
          await axiosInstance.post(APIs.POST_ADD_REQUISITION_LINE, {
            Requisition_ID: parseInt(requisitionId, 10),
            MenuItem_ID: mid,
            MenuItem_Name: mname,
            Unit: p.menuItem_DefaultSizeUnit ?? p.MenuItem_DefaultSizeUnit ?? p.unit ?? "Unit",
            RequiredQty: 1,
            DefaultSize: p.menuItem_DefaultSize ?? p.MenuItem_DefaultSize,
            DefaultSizeUnit: p.menuItem_DefaultSizeUnit ?? p.MenuItem_DefaultSizeUnit,
            ContainerType: p.menuItem_PurchaseContainerType ?? p.ContainerType ?? "Individual",
            ContainerQuantity: p.menuItem_PurchaseContainerQty ?? p.ContainerQuantity ?? 1,
          }, { params: { compId: compID } });
        }
        showSuccessToast("Products added successfully.");
        setShowModal(false);
        if (onRefresh) await onRefresh();
        return true;
      } catch (e) {
        showErrorToast(e?.response?.data?.message ?? "Failed to add products.");
        return false;
      } finally {
        if (setLoading) setLoading(false);
      }
    },
    [requisitionId, branchCompId, parentCompID, compID, setShowModal, onRefresh, setLoading]
  );

  const removeLine = async (lineId) => {
    const line = lines.find((l) => (l.requisitionLine_ID ?? l.RequisitionLine_ID) === lineId);
    if (!line) return;
    const ok = await swal({
      title: "Remove line?",
      text: `Remove "${getDisplayName(line)}" from this requisition?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!ok) return;
    try {
      setUpdatingLineId(lineId);
      if (setLoading) setLoading(true);
      await axiosInstance.delete(APIs.DELETE_REMOVE_REQUISITION_LINE, { params: { requisitionLineId: lineId } });
      showSuccessToast("Line removed.");
      if (onRefresh) await onRefresh();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Failed to remove line.");
    } finally {
      setUpdatingLineId(null);
      if (setLoading) setLoading(false);
    }
  };

  const handleEditClick = (line) => {
    setEditingLine(line);
    setEditRequiredQty(String(line.requiredQty ?? line.RequiredQty ?? 0));
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editingLine || !editRequiredQty) return;
    const q = parseFloat(editRequiredQty);
    if (isNaN(q) || q < 0) {
      showErrorToast("Enter a valid required quantity.");
      return;
    }
    const lid = editingLine.requisitionLine_ID ?? editingLine.RequisitionLine_ID;
    try {
      setUpdatingLineId(lid);
      if (setLoading) setLoading(true);
      await axiosInstance.put(APIs.PUT_UPDATE_REQUISITION_LINE, null, {
        params: { requisitionLineId: lid, requiredQty: q, compId: compID },
      });
      showSuccessToast("Quantity updated.");
      setShowEditModal(false);
      setEditingLine(null);
      setLineUpdates((prev) => {
        const next = { ...prev };
        delete next[lid];
        return next;
      });
      if (onRefresh) await onRefresh();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Update failed.");
    } finally {
      setUpdatingLineId(null);
      if (setLoading) setLoading(false);
    }
  };

  const handleRequiredQtyChange = (lineId, val) => {
    setLineUpdates((prev) => ({ ...prev, [lineId]: val }));
  };

  const saveRequiredQtyBatch = async () => {
    const entries = Object.entries(lineUpdates).filter(([, v]) => v != null && v !== "");
    if (entries.length === 0) return;
    try {
      if (setLoading) setLoading(true);
      for (const [lid, val] of entries) {
        const q = parseFloat(val);
        if (!isNaN(q) && q >= 0) {
          await axiosInstance.put(APIs.PUT_UPDATE_REQUISITION_LINE, null, {
            params: { requisitionLineId: lid, requiredQty: q, compId: compID },
          });
        }
      }
      setLineUpdates({});
      showSuccessToast("Quantities updated.");
      if (onRefresh) await onRefresh();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Update failed.");
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  const saveSendQtyBatch = async () => {
    if (!lines || lines.length === 0) return;
    try {
      setSavingSendQty(true);
      if (setLoading) setLoading(true);
      for (const l of lines) {
        const lid = l.requisitionLine_ID ?? l.RequisitionLine_ID;
        const sendQty = parseFloat(l.sendQty ?? l.SendQty ?? 0) || 0;
        await axiosInstance.put(APIs.PUT_SAVE_REQUISITION_LINE_SEND_QTY, null, {
          params: { requisitionLineId: lid, sendQty },
        });
      }
      showSuccessToast("Send quantities saved.");
      if (onRefresh) await onRefresh();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Failed to save Send quantities.");
    } finally {
      setSavingSendQty(false);
      if (setLoading) setLoading(false);
    }
  };

  const saveReceiveQtyBatch = async () => {
    if (!lines || lines.length === 0) return;
    try {
      setSavingReceiveQty(true);
      if (setLoading) setLoading(true);
      for (const l of lines) {
        const lid = l.requisitionLine_ID ?? l.RequisitionLine_ID;
        const receivedQty = parseFloat(l.receivedQty ?? l.ReceivedQty ?? 0) || 0;
        await axiosInstance.put(APIs.PUT_SAVE_REQUISITION_LINE_RECEIVED_QTY, null, {
          params: { requisitionLineId: lid, receivedQty },
        });
      }
      showSuccessToast("Receive quantities saved.");
      if (onRefresh) await onRefresh();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Failed to save Receive quantities.");
    } finally {
      setSavingReceiveQty(false);
      if (setLoading) setLoading(false);
    }
  };

  const updateLocalSendQty = (lineId, val) => {
    setLines((prev) =>
      prev.map((l) => {
        const lid = l.requisitionLine_ID ?? l.RequisitionLine_ID;
        if (lid !== parseInt(lineId, 10)) return l;
        return { ...l, sendQty: val, SendQty: val };
      })
    );
  };

  const updateLocalReceivedQty = (lineId, val) => {
    setLines((prev) =>
      prev.map((l) => {
        const lid = l.requisitionLine_ID ?? l.RequisitionLine_ID;
        if (lid !== parseInt(lineId, 10)) return l;
        return { ...l, receivedQty: val, ReceivedQty: val };
      })
    );
  };

  const filteredProducts = (products || []).filter((p) => {
    const pid = p.menuItem_ID ?? p.MenuItem_ID;
    if (lines.some((l) => (l.menuItem_ID ?? l.MenuItem_ID) === pid)) return false;
    const type = (p.menuItem_Type ?? p.MenuItem_Type ?? "").toString().toLowerCase();
    const hasVar = !!(p.menuItem_HasVariant ?? p.MenuItem_HasVariant);
    if (type === "product" && hasVar) return false;
    if (type === "combo") return false;
    return true;
  });

  const stockLabel = isMain ? "Current Stock" : "Branch stock";
  const colCount = 7 + (isAccepted || isSent ? 1 : 0) + (isDraft ? 1 : 0);

  return (
    <>
      <div className="mt-3">
        {lines.length > 0 ? (
          <>
            {canEditRequiredQty &&
              !(isAccepted && isMain) &&
              Object.keys(lineUpdates).length > 0 && (
              <div className="mb-3 d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  style={{ backgroundColor: themeColor, borderColor: themeColor }}
                  onClick={saveRequiredQtyBatch}
                >
                  Save quantities
                </button>
              </div>
            )}
            {isAccepted && isMain && lines.length > 0 && (
              <div className="mb-3 d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  style={{ backgroundColor: themeColor, borderColor: themeColor }}
                  onClick={saveSendQtyBatch}
                  disabled={savingSendQty}
                >
                  {savingSendQty ? "Saving..." : "Save Quantity"}
                </button>
              </div>
            )}
            {isSent && lines.length > 0 && (
              <div className="mb-3 d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  style={{ backgroundColor: themeColor, borderColor: themeColor }}
                  onClick={saveReceiveQtyBatch}
                  disabled={savingReceiveQty}
                >
                  {savingReceiveQty ? "Saving…" : "Save Receive quantities"}
                </button>
              </div>
            )}
            <div
              className="table-responsive table-container"
              style={{
                minHeight: "200px",
                maxHeight: "450px",
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
                zIndex: 1,
              }}
            >
              <table
                className="table table-hover"
                style={{ position: "relative" }}
              >
                <thead
                  className="bg-light sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10000,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr
                    style={{
                      borderBottom: `2px solid ${themeColor}`,
                    }}
                  >
                    <th className="pt-2 pb-2" style={{ fontWeight: "600" }}>Item Name</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Unit</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>{stockLabel}</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Required (Demand)</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Send</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Received</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Difference</th>
                    {(isAccepted || isSent) && (
                      <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Cost</th>
                    )}
                    {isDraft && (
                      <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const lid = l.requisitionLine_ID ?? l.RequisitionLine_ID;
                    const req =
                      lineUpdates[lid] !== undefined
                        ? lineUpdates[lid]
                        : (l.requiredQty ?? l.RequiredQty ?? 0);
                    const send = l.sendQty ?? l.SendQty ?? 0;
                    const recv = l.receivedQty ?? l.ReceivedQty ?? 0;
                    const diff = (parseFloat(send) || 0) - (parseFloat(recv) || 0);
                    const name = getDisplayName(l);
                    return (
                      <tr
                        key={lid}
                        draggable={false}
                      >
                        <td className="align-middle" style={{ padding: "12px", minWidth: "140px" }}>
                          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                background: isDarkMode ? "#4a5568" : "#f8f9fa",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: isDarkMode ? "1px solid rgba(255,255,255,0.2)" : "1px solid #e9ecef",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              {l.menuItem_Image || l.MenuItem_Image ? (
                                <LazyLoadImage
                                  src={l.menuItem_Image ?? l.MenuItem_Image}
                                  alt={name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <i
                                  className="fa fa-box"
                                  style={{ fontSize: "14px", color: isDarkMode ? "#a0aec0" : "#6c757d" }}
                                />
                              )}
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                flex: "1 1 auto",
                                minWidth: 0,
                                wordBreak: "break-word",
                                color: isDarkMode ? "#ffffff" : "#212529",
                              }}
                            >
                              {name}
                            </div>
                          </div>
                        </td>
                        <td className="align-middle text-center" style={{ padding: "12px" }}>
                          {getLineUnit(l)}
                        </td>
                        <td className="align-middle text-center" style={{ padding: "12px" }}>
                          {(() => {
                            const currentQty = l.current_Quantity ?? l.Current_Quantity ?? 0;
                            const measurementType = l.menuItem_MeasurementType ?? l.MenuItem_MeasurementType ?? "";
                            const defaultSizeUnit = l.menuItem_DefaultSizeUnit ?? l.MenuItem_DefaultSizeUnit ?? l.defaultSizeUnit ?? l.DefaultSizeUnit ?? "";
                            const unit = l.unit ?? l.Unit ?? "";
                            const defaultSize = parseFloat(l.menuItem_DefaultSize ?? l.MenuItem_DefaultSize ?? l.defaultSize ?? l.DefaultSize ?? 1) || 1;
                            let displayQty = parseFloat(currentQty) || 0;
                            if (measurementType && measurementType !== "Unit" && defaultSize > 0) {
                              displayQty = displayQty * defaultSize;
                            }
                            let unitLabel = "";
                            if (measurementType && measurementType !== "Unit") {
                              unitLabel = defaultSizeUnit || unit || (measurementType === "Weight" ? "kg" : measurementType === "Volume" ? "L" : "");
                            } else {
                              unitLabel = unit || defaultSizeUnit || "";
                            }
                            return (
                              <span>
                                {displayQty.toFixed(2)}{unitLabel ? ` ${unitLabel}` : ""}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="align-middle text-center" style={{ padding: "12px" }}>
                          {canEditRequiredQty ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "90px", margin: "0 auto" }}
                              min="0"
                              step="any"
                              value={req}
                              onChange={(e) => handleRequiredQtyChange(lid, e.target.value)}
                            />
                          ) : (
                            req
                          )}
                        </td>
                        <td className="align-middle text-center" style={{ padding: "12px" }}>
                          {isAccepted && isMain ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "90px", margin: "0 auto" }}
                              min="0"
                              step="any"
                              value={send || ""}
                              onChange={(e) => updateLocalSendQty(lid, e.target.value)}
                              placeholder="0"
                            />
                          ) : (
                            send && send > 0 ? send : "—"
                          )}
                        </td>
                        <td className="align-middle text-center" style={{ padding: "12px" }}>
                          {isSent ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "90px", margin: "0 auto" }}
                              min="0"
                              step="any"
                              value={recv}
                              onChange={(e) => updateLocalReceivedQty(lid, e.target.value)}
                            />
                          ) : (
                            recv || "—"
                          )}
                        </td>
                        <td className="align-middle text-center" style={{ padding: "12px" }}>
                          {diff !== 0 ? diff : "—"}
                        </td>
                        {(isAccepted || isSent) && (
                          <td className="align-middle text-center" style={{ padding: "12px" }}>
                            {l.costPrice ?? l.CostPrice ?? "—"}
                          </td>
                        )}
                        {isDraft && (
                          <td className="align-middle text-center" style={{ padding: "12px" }}>
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                onClick={() => handleEditClick(l)}
                                className="btn btn-sm btn-outline-primary"
                                disabled={updatingLineId === lid}
                                title="Edit quantity"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "6px",
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.color = "white";
                                  e.currentTarget.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = themeColor;
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                {updatingLineId === lid ? (
                                  <i className="fa fa-spinner fa-spin" style={{ fontSize: "12px" }} />
                                ) : (
                                  <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                                )}
                              </button>
                              <button
                                onClick={() => removeLine(lid)}
                                className="btn btn-sm btn-outline-danger"
                                disabled={updatingLineId === lid}
                                title="Remove"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "6px",
                                  border: "1px solid #dc3545",
                                  color: "#dc3545",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#dc3545";
                                  e.currentTarget.style.color = "white";
                                  e.currentTarget.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = "#dc3545";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                {updatingLineId === lid ? (
                                  <i className="fa fa-spinner fa-spin" style={{ fontSize: "12px" }} />
                                ) : (
                                  <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                                )}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div
              style={{
                width: "60px",
                height: "60px",
                background: isDarkMode ? "#555" : "#f8f9fa",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                border: isDarkMode ? "1px solid #666" : "1px solid #e9ecef",
              }}
            >
              <i
                className="fa fa-box"
                style={{ fontSize: "24px", color: isDarkMode ? "#fff" : "#6c757d" }}
              />
            </div>
            <p style={{ color: isDarkMode ? "#fff" : "#6c757d", fontSize: "15px", marginBottom: "20px" }}>
              No products added to this requisition
            </p>
            {canAddItems && (
              <button
                onClick={() => setShowModal(true)}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: `1px solid ${themeColor}`,
                  boxShadow: `0 4px 15px ${themeColor}40`,
                  borderRadius: "8px",
                  padding: "10px 20px",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = `0 6px 20px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 4px 15px ${themeColor}40`;
                }}
              >
                <i className="fa-solid fa-plus" style={{ fontSize: "12px" }} />
                Add Products
              </button>
            )}
          </div>
        )}
      </div>

      {editingLine && (
        <CustomModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingLine(null);
          }}
          title="Edit Required Quantity"
          subtitle={getDisplayName(editingLine)}
          modalSize="modal-lg"
          showSubmitButton
          showCancelButton
          submitButtonText="Update"
          onSubmit={handleEditSubmit}
          isCentered
        >
          <FormInput
            label="Required quantity *"
            type="number"
            step="any"
            min="0"
            value={editRequiredQty}
            onchange={(e) => setEditRequiredQty(e.target.value)}
            placeHolder="0"
          />
        </CustomModal>
      )}

      <ProductMultiSelectionModal
        selectedProducts={[]}
        setSelectedProducts={() => {}}
        showModal={showModal}
        setShowModal={setShowModal}
        onSubmit={handleModalSubmit}
        products={filteredProducts}
        showButton={true}
        selectionPrompt="Select the products you want to add to this requisition"
        stockLabel={isMain ? "Current Stock" : "Branch stock"}
      />
    </>
  );
};

export default RequisitionProductSelector;
